import React from 'react'
import { Element } from 'react-bulma-components'
import { isSameDay } from 'date-fns'
import { Clocking } from '../../api/clockings'
import { WorkPeriod } from '../../api/work-periods'
import { buildClockingsTimeline, sourceToLabel, TimelineEvent } from '../../utils/clockings'
import { formatTime, formatDayName } from '../../utils/date'

interface Props {
  /** All clockings to display (registered, orphaned, any mix). */
  clockings: Clocking[]
  /** Optional WP — its theoretical/clocked/manual times are added to the timeline. */
  workPeriod?: WorkPeriod
}

/**
 * Renders a chronological timeline as a borderless table.
 * Columns: Day | Time | Type (Théorique, Pointage, Correction) | Role (Entrée, Sortie, etc.)
 * The "counted" row is bold + green (success).
 * Day name only shown on the first row of each day.
 *
 * Shared between WP details (Pointages section) and the orphan context modal (DaySection).
 */
const ClockingsTimeline: React.FC<Props> = ({ clockings, workPeriod }) => {
  const events = buildClockingsTimeline(clockings, workPeriod)

  if (events.length === 0) {
    return (
      <Element textSize={6} textColor="grey">
        Aucun pointage ni journée
      </Element>
    )
  }

  return (
    <table style={{ borderCollapse: 'collapse' }}>
      <tbody>
        {events.map((event, i) => {
          const prevEvent = i > 0 ? events[i - 1] : null
          const showDay = !prevEvent || !isSameDay(event.time, prevEvent.time)
          return <TimelineRow key={`${event.source}-${i}`} event={event} showDay={showDay} />
        })}
      </tbody>
    </table>
  )
}

const cellStyle: React.CSSProperties = {
  border: 'none',
  padding: '2px 8px 2px 0',
  verticalAlign: 'top',
}

const TimelineRow: React.FC<{ event: TimelineEvent; showDay: boolean }> = ({ event, showDay }) => {
  const boldStyle: React.CSSProperties = {
    ...cellStyle,
    fontWeight: event.isCounted ? 'bold' : undefined,
  }

  return (
    <tr>
      <td style={cellStyle}>{showDay ? formatDayName(event.time) : ''}</td>
      <td style={boldStyle}>{formatTime(event.time)}</td>
      <td style={cellStyle}>{sourceToLabel(event.source)}</td>
      <td style={cellStyle}>{event.role}</td>
      <td style={boldStyle}>{event.isCounted ? '✓ Pris en compte' : ''}</td>
    </tr>
  )
}

export default ClockingsTimeline
